import moment from "moment";
import { SERVICES, SERVICE_ORDER, ROLES, ROLE_ORDER } from "../backofficeConstants";

/**
 * Pure helpers behind the client page's Backoffice tab. An "engagement" here is what the API's
 * /backoffice/engagement/list hands back: one client's tasks rolled up by service and period, with
 * the member tasks attached. Everything in this file is derived from that list; nothing is stored.
 */

const DATE = "YYYY-MM-DD";

export const ENGAGEMENT_STATUSES = {
	not_started: { key: "not_started", label: "Not Started", cls: "notstarted" },
	in_progress: { key: "in_progress", label: "In Progress", cls: "progress" },
	pending_client: { key: "pending_client", label: "Pending Client", cls: "pending" },
	overdue: { key: "overdue", label: "Overdue", cls: "overdue" },
	complete: { key: "complete", label: "Complete", cls: "complete" },
};

export const ENGAGEMENT_STATUS_ORDER = ["not_started", "in_progress", "pending_client", "overdue", "complete"];

export function round(n, places = 2) {
	const f = Math.pow(10, places);
	return Math.round((n || 0) * f) / f;
}

export function pct(part, whole) {
	if (!whole) return 0;
	return Math.round((part / whole) * 100);
}

/**
 * Hours the way the tab prints them: whole numbers stay whole, otherwise two decimals at most.
 */
export function hours(n) {
	const v = round(n, 2);
	return Number.isInteger(v) ? String(v) : String(v).replace(/0$/, "");
}

export function isOpen(engagement) {
	return engagement.status !== "complete";
}

/**
 * Open engagements first by due date (soonest first), then completed ones newest first.
 */
export function splitOpenCompleted(engagements) {
	const open = engagements.filter(isOpen).sort((a, b) => String(a.dueDate || "").localeCompare(String(b.dueDate || "")) || String(b.periodStart).localeCompare(String(a.periodStart)));
	const completed = engagements
		.filter(e => !isOpen(e))
		.sort((a, b) => String(b.completedAt || b.periodStart).localeCompare(String(a.completedAt || a.periodStart)) || String(b.periodStart).localeCompare(String(a.periodStart)));
	return { open, completed };
}

/**
 * The numbers in the tiles above the table, for whatever set of engagements is on screen.
 */
export function summarize(engagements) {
	const out = { engagements: engagements.length, open: 0, tasksDone: 0, tasksTotal: 0, logged: 0, budget: 0, onTimeDone: 0, onTimeTotal: 0, overdueTasks: 0, overdueEngagements: 0 };
	engagements.forEach(e => {
		if (isOpen(e)) out.open++;
		out.tasksDone += e.completeCount || 0;
		out.tasksTotal += e.taskCount || 0;
		out.logged += (e.logged && e.logged.total) || 0;
		out.budget += (e.budget && e.budget.total) || 0;
		if (e.onTime === true || e.onTime === false) {
			out.onTimeTotal++;
			if (e.onTime) out.onTimeDone++;
		}
		out.overdueTasks += e.overdueCount || 0;
		if (e.status === "overdue") out.overdueEngagements++;
	});
	out.logged = round(out.logged);
	out.budget = round(out.budget);
	return out;
}

/**
 * The period filter's default window: the start of the month three months back through the end
 * of the current month, so a client's recent history and current work are both in view.
 */
export function defaultRange(today = moment()) {
	const t = moment(today);
	return { from: t.clone().subtract(3, "months").startOf("month").format(DATE), to: t.clone().endOf("month").format(DATE) };
}

/**
 * Month choices for the From / To selects: the current month plus `back` earlier months and
 * `forward` later ones, newest first.
 */
export function monthOptions({ back = 18, forward = 2, today = moment() } = {}) {
	const out = [];
	const start = moment(today).startOf("month").add(forward, "months");
	for (let i = 0; i < back + forward + 1; i++) {
		const m = start.clone().subtract(i, "months");
		out.push({ key: m.format("YYYY-MM"), label: m.format("MMM YYYY"), start: m.format(DATE), end: m.clone().endOf("month").format(DATE) });
	}
	return out;
}

export function monthKey(dateStr) {
	return moment(dateStr, DATE).format("YYYY-MM");
}

function emptyServiceHours() {
	const out = {};
	SERVICE_ORDER.forEach(s => (out[s] = { logged: 0, budget: 0, engagements: 0 }));
	return out;
}

/**
 * Report: one row per calendar month (by period start), with hours split by service.
 */
export function reportByMonth(engagements) {
	const byKey = {};
	engagements.forEach(e => {
		const key = monthKey(e.periodStart);
		if (!byKey[key]) {
			byKey[key] = { key, label: moment(key, "YYYY-MM").format("MMMM YYYY"), engagements: 0, open: 0, tasksDone: 0, tasksTotal: 0, logged: 0, budget: 0, onTimeDone: 0, onTimeTotal: 0, byService: emptyServiceHours() };
		}
		const row = byKey[key];
		row.engagements++;
		if (isOpen(e)) row.open++;
		row.tasksDone += e.completeCount || 0;
		row.tasksTotal += e.taskCount || 0;
		const logged = (e.logged && e.logged.total) || 0;
		const budget = (e.budget && e.budget.total) || 0;
		row.logged += logged;
		row.budget += budget;
		if (row.byService[e.service]) {
			row.byService[e.service].logged += logged;
			row.byService[e.service].budget += budget;
			row.byService[e.service].engagements++;
		}
		if (e.onTime === true || e.onTime === false) {
			row.onTimeTotal++;
			if (e.onTime) row.onTimeDone++;
		}
	});
	const rows = Object.keys(byKey)
		.sort()
		.map(k => {
			const r = byKey[k];
			r.logged = round(r.logged);
			r.budget = round(r.budget);
			r.variance = round(r.logged - r.budget);
			SERVICE_ORDER.forEach(s => {
				r.byService[s].logged = round(r.byService[s].logged);
				r.byService[s].budget = round(r.byService[s].budget);
			});
			return r;
		});
	return rows;
}

/**
 * Report: one row per service.
 */
export function reportByService(engagements) {
	return SERVICE_ORDER.map(s => {
		const mine = engagements.filter(e => e.service === s);
		const row = { service: s, label: SERVICES[s].label, color: SERVICES[s].color, engagements: mine.length, tasksDone: 0, tasksTotal: 0, logged: 0, budget: 0, overdueTasks: 0 };
		mine.forEach(e => {
			row.tasksDone += e.completeCount || 0;
			row.tasksTotal += e.taskCount || 0;
			row.logged += (e.logged && e.logged.total) || 0;
			row.budget += (e.budget && e.budget.total) || 0;
			row.overdueTasks += e.overdueCount || 0;
		});
		row.logged = round(row.logged);
		row.budget = round(row.budget);
		row.budgetUsePct = pct(row.logged, row.budget);
		return row;
	}).filter(r => r.engagements > 0);
}

/**
 * Report: hours by role, with each role's share of logged time.
 */
export function reportByRole(engagements) {
	const rows = ROLE_ORDER.map(r => ({ role: r, label: ROLES[r].label, logged: 0, budget: 0, share: 0 }));
	engagements.forEach(e => {
		rows.forEach(row => {
			row.logged += (e.logged && e.logged[row.role]) || 0;
			row.budget += (e.budget && e.budget[row.role]) || 0;
		});
	});
	const total = rows.reduce((n, r) => n + r.logged, 0);
	rows.forEach(r => {
		r.logged = round(r.logged);
		r.budget = round(r.budget);
		r.share = pct(r.logged, total);
	});
	return rows;
}

/**
 * Report: one row per task that has a client email automation, with what its schedule did.
 */
export function emailRows(engagements) {
	const out = [];
	engagements.forEach(e => {
		(e.tasks || []).forEach(t => {
			const a = t.emailAutomation;
			if (!a) return;
			const sends = a.sends || [];
			const sent = sends.filter(s => s.status === "sent");
			const scheduled = sends.filter(s => s.status === "scheduled");
			out.push({
				key: `${e.id}-${t.id}`,
				engagementName: `${e.serviceLabel} · ${e.periodLabel}`,
				taskTitle: t.title,
				scheduled: sends.length,
				sent: sent.length,
				skipped: sends.filter(s => s.status === "skipped").length,
				failed: sends.filter(s => s.status === "failed").length,
				firstSend: sends.length ? sends[0].scheduledFor : null,
				lastSent: sent.length ? sent[sent.length - 1].scheduledFor : null,
				nextSend: scheduled.length ? scheduled[0].scheduledFor : null,
				status: a.status,
				taskComplete: t.status === "complete",
			});
		});
	});
	return out;
}

/**
 * The small line under a due date: how far away it is for open work, when it was finished for
 * completed work.
 */
export function dueNote(engagement, today = moment().format(DATE)) {
	if (!isOpen(engagement)) {
		if (!engagement.completedAt) return "done";
		const late = engagement.onTime === false;
		return `done ${moment(engagement.completedAt, DATE).format("MMM D")}${late ? " · late" : ""}`;
	}
	if (!engagement.dueDate) return "";
	const days = moment(engagement.dueDate, DATE).diff(moment(today, DATE), "days");
	if (days === 0) return "today";
	if (days === 1) return "tomorrow";
	if (days === -1) return "yesterday";
	if (days > 1) return `in ${days} days`;
	return `${-days} days ago`;
}

/**
 * Member tasks in display order, bundled under their group names; ungrouped tasks last.
 */
export function groupTasks(tasks) {
	const groups = [];
	const byName = {};
	let ungrouped = null;
	(tasks || []).forEach(t => {
		const name = t.groupName || null;
		if (!name) {
			if (!ungrouped) ungrouped = { groupName: null, groupIcon: null, tasks: [] };
			ungrouped.tasks.push(t);
			return;
		}
		if (!byName[name]) {
			byName[name] = { groupName: name, groupIcon: t.groupIcon || null, tasks: [] };
			groups.push(byName[name]);
		}
		byName[name].tasks.push(t);
	});
	if (ungrouped) groups.push(ungrouped);
	return groups;
}

export function taskDueNote(task, today = moment().format(DATE)) {
	if (task.status === "complete") return "done";
	if (!task.dueDate) return "";
	const days = moment(today, DATE).diff(moment(task.dueDate, DATE), "days");
	if (days > 0) return `${days}d late`;
	if (days === 0) return "due today";
	return "open";
}
